import type { Locale } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import enGB from 'date-fns/locale/en-GB';
import frCA from 'date-fns/locale/fr-CA';
import es from 'date-fns/locale/es';
import nl from 'date-fns/locale/nl';
import de from 'date-fns/locale/de';

import type { NzI18nInterface } from 'ng-zorro-antd/i18n';
import { en_US, en_GB, fr_FR, es_ES, nl_NL, de_DE } from 'ng-zorro-antd/i18n';

import localesMeta from './supported-languages.json';
import { CookieKey } from '../cookies/cookie-keys.const';

/**
 * Full language metadata (single source for compile-time keys). `LanguageCode` is derived from
 * these keys. `locales.config.json` is validated against the same keys at module load.
 */
const LANGUAGE_CONFIGS = {
  'en-US': {
    code: 'en-US',
    name: 'English (US)',
    keyNamespace: 'ENGLISH',
    momentLocale: 'en',
    dateFnsLocale: enUS,
    nzI18n: en_US,
    adaChatbotLocale: 'en',
    helpCenterLocale: 'en-us',
    thoughtSpotLocale: 'en-US',
    bulkEditsLocale: 'en',
  },
  'fr-CA': {
    code: 'fr-CA',
    name: 'Français (CA)',
    keyNamespace: 'FRENCH',
    momentLocale: localesMeta.momentLocaleMap['fr-CA'],
    dateFnsLocale: frCA,
    nzI18n: fr_FR,
    adaChatbotLocale: 'fr',
    helpCenterLocale: 'fr-ca',
    thoughtSpotLocale: 'fr-CA',
    bulkEditsLocale: 'fr',
  },
  'es-MX': {
    code: 'es-MX',
    name: 'Español (MX)',
    keyNamespace: 'SPANISH',
    momentLocale: localesMeta.momentLocaleMap['es-MX'],
    dateFnsLocale: es,
    nzI18n: es_ES,
    adaChatbotLocale: 'es',
    helpCenterLocale: 'es-mx',
    thoughtSpotLocale: 'es-MX',
    bulkEditsLocale: 'es',
  },
  'nl-NL': {
    code: 'nl-NL',
    name: 'Nederlands',
    keyNamespace: 'DUTCH',
    momentLocale: localesMeta.momentLocaleMap['nl-NL'],
    dateFnsLocale: nl,
    nzI18n: nl_NL,
    adaChatbotLocale: 'nl',
    helpCenterLocale: 'nl-nl',
    thoughtSpotLocale: 'nl-NL',
    bulkEditsLocale: 'nl-NL',
  },
  'en-GB': {
    code: 'en-GB',
    name: 'English (UK)',
    keyNamespace: 'ENGLISH_GB',
    momentLocale: localesMeta.momentLocaleMap['en-GB'],
    dateFnsLocale: enGB,
    nzI18n: en_GB,
    adaChatbotLocale: 'en',
    helpCenterLocale: 'en-gb',
    thoughtSpotLocale: 'en-GB',
    bulkEditsLocale: 'en-GB',
  },
  'de-DE': {
    code: 'de-DE',
    name: 'Deutsch',
    keyNamespace: 'GERMAN',
    momentLocale: localesMeta.momentLocaleMap['de-DE'],
    dateFnsLocale: de,
    nzI18n: de_DE,
    adaChatbotLocale: 'de',
    helpCenterLocale: 'de',
    thoughtSpotLocale: 'de-DE',
    bulkEditsLocale: 'de-DE',
  },
} as const;

export type LanguageCode = keyof typeof LANGUAGE_CONFIGS;

type LanguageCodeByName = {
  [K in LanguageCode as typeof LANGUAGE_CONFIGS[K]['keyNamespace']]: K;
};

export interface LanguageConfig {
  code: LanguageCode;
  name: string;
  keyNamespace: string;
  momentLocale: string;
  dateFnsLocale: Locale;
  nzI18n: NzI18nInterface;
  adaChatbotLocale: string;
  helpCenterLocale: string;
  thoughtSpotLocale: string;
  bulkEditsLocale: string;
}

const languageCodesFromJson = new Set(localesMeta.supportedLanguages);
const languageCodesFromMap = new Set(Object.keys(LANGUAGE_CONFIGS));

if (
  languageCodesFromJson.size !== languageCodesFromMap.size ||
  [...languageCodesFromJson].some((c) => !languageCodesFromMap.has(c as LanguageCode))
) {
  throw new Error(
    `Supported languages mismatch: ${[...languageCodesFromJson].join(', ')} vs ${[...languageCodesFromMap].join(', ')}`,
  );
}

if (!(LANGUAGE_CONFIGS[localesMeta.sourceLanguage as LanguageCode])) {
  throw new Error(
    `Source language ${localesMeta.sourceLanguage} not found in LANGUAGE_CONFIGS`,
  );
}


/**
 * @deprecated Once `LocalizationService` (app/locale-formatters) is fully onboarded and all
 * usages of Angular's built-in formatting pipes (DatePipe, DecimalPipe, CurrencyPipe,
 * PercentPipe) and ng-zorro's locale infrastructure are migrated to date-fns, this map and the
 * `registerLocaleData` call in utils.ts can be removed entirely.
 * Until then, each entry must be kept in sync with LANGUAGE_CONFIGS.
 */
export const ANGULAR_LOCALE_IMPORTS: Partial<Record<LanguageCode, () => Promise<{ default: unknown }>>> = {
  'fr-CA': () => import('@angular/common/locales/fr-CA'),
  'es-MX': () => import('@angular/common/locales/es-MX'),
  'nl-NL': () => import('@angular/common/locales/nl'),
  'en-GB': () => import('@angular/common/locales/en-GB'),
  'de-DE': () => import('@angular/common/locales/de'),
};

export const DEFAULT_LANGUAGE: LanguageCode = LANGUAGE_CONFIGS[localesMeta.sourceLanguage as LanguageCode].code;
export const DEFAULT_LANGUAGE_CONFIG: LanguageConfig = LANGUAGE_CONFIGS[DEFAULT_LANGUAGE];


export function getLanguageConfig(code: string): LanguageConfig | undefined {
  return LANGUAGE_CONFIGS[code as LanguageCode] ?? undefined;
}


export const I18N_COOKIE_KEY: CookieKey = CookieKey.I18N_LANG;

/**
 * Normalizes raw `i18n_lang` cookie values to canonical `LanguageCode`.
 * - Legacy shorthand: only `en` maps to `en-US`.
 * - All supported languages also map 1:1 by BCP-47 code (e.g. `en-GB` → `en-GB`).
 * Needs to be maintained in locale-detection.ejs as well
 */
export const I18N_LANG_COOKIE_MAP: Record<string, string> = {
  en: 'en-US',
  ...Object.fromEntries(Object.keys(LANGUAGE_CONFIGS).map((key) => [key, key])),
};

/**
 * A map of language key namespaces to language codes.
 */
export const LANGUAGES: LanguageCodeByName = Object.fromEntries(
  (Object.keys(LANGUAGE_CONFIGS) as LanguageCode[]).map(
    (code) => [LANGUAGE_CONFIGS[code].keyNamespace, code],
  ),
) as LanguageCodeByName;

export const SUPPORTED_LANGUAGES: Record<LanguageCode, string> = Object.keys(LANGUAGE_CONFIGS).reduce((acc, key) => {
  acc[key as LanguageCode] = LANGUAGE_CONFIGS[key as LanguageCode].name;
  return acc;
}, {} as Record<LanguageCode, string>);

/**
 * @deprecated Prefer `SupportedLocale` from the app locale formatting layer (`locale.types`,
 * `LocalizationService`). This enum is kept for compatibility during migration; new and updated
 * code should use the `SupportedLocale` mapping used by the locale formatting service instead of
 * `LOCALES`.
 */
export enum LOCALES {
  ENGLISH = 'en-US',
  FRENCH = 'fr-CA',
  SPANISH = 'es-MX',
}
